import React, { useState, useEffect, useRef } from 'react';
import './Viewer.css';

function Viewer({ projectId }) {
  const [project, setProject] = useState(null);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [audio, setAudio] = useState(null);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [expandedMedia, setExpandedMedia] = useState(null);
  const selectorRef = useRef(null);

  useEffect(() => {
    fetch(`/data/${projectId}.json`)
      .then(res => res.json())
      .then(data => {
        setProject(data);
      })
      .catch(err => console.error('Failed to load project:', err));
  }, [projectId]);

  useEffect(() => {
    return () => {
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
    };
  }, [currentSectionIndex, audio]);

  // Track scroll position to update highlight
  useEffect(() => {
    const selector = selectorRef.current;
    if (!selector || !project) return;

    const handleScroll = () => {
      const items = selector.querySelectorAll('.section-item');
      const selectorCenter = selector.scrollLeft + selector.offsetWidth / 2;
      
      let closestIndex = 0;
      let closestDistance = Infinity;
      
      items.forEach((item, index) => {
        const itemCenter = item.offsetLeft + item.offsetWidth / 2;
        const distance = Math.abs(itemCenter - selectorCenter);
        
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });
      
      setCurrentSectionIndex(closestIndex);
    };

    selector.addEventListener('scroll', handleScroll);
    return () => selector.removeEventListener('scroll', handleScroll);
  }, [project]);

  if (!project) {
    return <div className="loading">Loading artwork...</div>;
  }

  const currentSection = project.sections[currentSectionIndex];
  const sectionPositions = project.layout.section_positions[currentSectionIndex];

  // Calculate precise character-level highlight rectangles (tighter fit)
  const getHighlightRectangles = () => {
    if (!sectionPositions || !sectionPositions.rows) return [];
    
    const { output_width, output_height } = project.layout;
    
    return sectionPositions.rows.map(row => {
        const topPercent = (row.y_px / output_height) * 100;
        const heightPx = parseInt(project.preset.font_size_pt * project.preset.dpi / 72 * project.preset.line_height_multiplier);
        const heightPercent = (heightPx / output_height) * 100;
        
        // GOLDILOCKS ZONE: Exact pixels + tiny padding
        const leftPercent = (row.x_start_px / output_width) * 100 - 0.2;
        const widthPercent = ((row.x_end_px - row.x_start_px) / output_width) * 100 + 0.2;
        
        return {
        top: `${topPercent}%`,
        height: `${heightPercent}%`,
        left: `${leftPercent}%`,
        width: `${widthPercent}%`,
        };
    });
  };

  const scrollToSection = (index) => {
    const selector = selectorRef.current;
    if (!selector) return;
    
    const items = selector.querySelectorAll('.section-item');
    const targetItem = items[index];
    if (!targetItem) return;
    
    const itemCenter = targetItem.offsetLeft + targetItem.offsetWidth / 2;
    const selectorCenter = selector.offsetWidth / 2;
    const scrollPosition = itemCenter - selectorCenter;
    
    selector.scrollTo({
      left: scrollPosition,
      behavior: 'smooth'
    });
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setExpandedMedia(null);
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
      setIsAudioPlaying(false);
    }
  };

  const toggleAudio = () => {
    if (!currentSection.media?.primary_audio) return;
    
    if (isAudioPlaying && audio) {
      audio.pause();
      setIsAudioPlaying(false);
    } else {
      if (audio) {
        audio.pause();
      }
      const newAudio = new Audio(currentSection.media.primary_audio);
      newAudio.play();
      newAudio.onended = () => setIsAudioPlaying(false);
      setAudio(newAudio);
      setIsAudioPlaying(true);
    }
  };

  const toggleMediaExpand = (type, index) => {
    setExpandedMedia(expandedMedia === `${type}-${index}` ? null : `${type}-${index}`);
  };

  return (
    <div className="viewer">
      {/* Section Selector at TOP */}
      <div className="section-selector" ref={selectorRef}>
        {project.sections.map((section, index) => (
          <div
            key={section.id}
            className={`section-item ${index === currentSectionIndex ? 'active' : ''}`}
            onClick={() => {
              setCurrentSectionIndex(index);
              scrollToSection(index);
              setIsModalOpen(true);
            }}
          >
            <span className="section-number">{index + 1}</span>
            <span className="section-title">{section.title || `Section ${index + 1}`}</span>
          </div>
        ))}
      </div>

      {/* Portrait with precise character-level highlighting */}
      <div className="portrait-container">
        <img 
          src={`/images/${projectId}.png`}
          alt={project.title}
          className="portrait-image"
        />
        {getHighlightRectangles().map((style, idx) => (
          <div 
            key={idx}
            className="highlight-line"
            style={style}
          />
        ))}
      </div>

      {/* Section Modal */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>✕</button>
            
            <h2>{currentSection.title || `Section ${currentSectionIndex + 1}`}</h2>
            
            {/* Media icons at TOP */}
            <div className="media-icons">
              {currentSection.media?.primary_audio && (
                <button 
                  className={`media-icon ${isAudioPlaying ? 'playing' : ''}`}
                  onClick={toggleAudio} 
                  title={isAudioPlaying ? "Stop Audio" : "Play Audio"}
                >
                  {isAudioPlaying ? '⏸️' : '🔊'}
                </button>
              )}
              {currentSection.media?.images?.map((img, idx) => (
                <button 
                  key={idx}
                  className="media-icon" 
                  onClick={() => toggleMediaExpand('image', idx)}
                  title="View Image"
                >
                  🖼️
                </button>
              ))}
              {currentSection.media?.videos?.map((vid, idx) => (
                <button 
                  key={idx}
                  className="media-icon" 
                  onClick={() => toggleMediaExpand('video', idx)}
                  title="Play Video"
                >
                  🎥
                </button>
              ))}
            </div>

            {/* Expanded media */}
            {expandedMedia && expandedMedia.startsWith('image-') && (
              <div className="expanded-media">
                <img src={currentSection.media.images[parseInt(expandedMedia.split('-')[1])]} alt="Section media" />
              </div>
            )}
            {expandedMedia && expandedMedia.startsWith('video-') && (
              <div className="expanded-media">
                <video src={currentSection.media.videos[parseInt(expandedMedia.split('-')[1])]} controls autoPlay />
              </div>
            )}

            {/* Text with smaller font */}
            <div className="modal-text">
              {currentSection.text}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Viewer;